import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Control,
  FieldValues,
  UseFormSetValue,
  useWatch
} from "react-hook-form";
import { useNavigate } from "react-router";

import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { format, isValid } from "date-fns";

import { misc, neutral } from "app/themes/colors";
import CheckboxFormField from "components/checkbox-form-field/index";
import {
  PATIENT_SEARCH_QUERY,
  useLazyQueryPatientsQuery
} from "services/patients";
import type { Patient } from "services/types/patients.type";
import { formatBackendDateOnly } from "utils/common";

import PatientSearchOption from "../../components/patient-search/patient-search-option.component";

type DuplicatedPatientWarningProps = {
  control: Control<any, object>;
  setValue: UseFormSetValue<any>;
  onSave?: any;
};

export default function DuplicatedPatientWarning({
  control,
  setValue,
  onSave
}: DuplicatedPatientWarningProps) {
  const navigate = useNavigate();
  const [searchDuplicatePatient] = useLazyQueryPatientsQuery();
  const [duplicatedPatient, setDuplicatedPatient] = useState<Patient | null>(
    null
  );

  const [firstName, lastName, birthdate] = useWatch<
    FieldValues,
    ["first_name", "last_name", "birthdate"]
  >({
    control,
    name: ["first_name", "last_name", "birthdate"]
  });
  const onClick = useCallback(() => {
    if (duplicatedPatient) {
      if (onSave) {
        onSave(duplicatedPatient);
      } else {
        navigate(`/patients/${duplicatedPatient.id}`);
      }
    }
  }, [onSave, duplicatedPatient, navigate]);
  const searchDuplicate = useMemo(
    () =>
      firstName && lastName && birthdate && isValid(new Date(birthdate))
        ? [firstName, lastName, format(new Date(birthdate), "MM/dd/yyyy")].join(
            " "
          )
        : "",
    [firstName, lastName, birthdate]
  );

  useEffect(() => {
    if (searchDuplicate) {
      searchDuplicatePatient({
        search: searchDuplicate,
        baseFilter: {},
        query: PATIENT_SEARCH_QUERY
      }).then(({ data }) => {
        const match = data?.find((patient) => {
          const patientFirstName = patient.first_name?.toLowerCase();
          const firstNameLower = firstName?.toLowerCase();
          const patientLastName = patient.last_name?.toLowerCase();
          const lastNameLower = lastName?.toLowerCase();

          return (
            (patientFirstName === firstNameLower ||
              patientFirstName.startsWith(firstNameLower) ||
              firstNameLower.startsWith(patientFirstName)) &&
            patientLastName === lastNameLower &&
            patient.birthdate &&
            birthdate &&
            formatBackendDateOnly(patient.birthdate, "MM/dd/yyyy") ===
              format(new Date(birthdate), "MM/dd/yyyy")
          );
        });

        setDuplicatedPatient(match || null);
        if (match) {
          setValue("allow_create", false, {
            shouldDirty: true,
            shouldValidate: true
          });
        } else {
          setValue("allow_create", true, {
            shouldDirty: true,
            shouldValidate: true
          });
        }
      });
    } else {
      setDuplicatedPatient(null);
      setValue("allow_create", true, {
        shouldDirty: true,
        shouldValidate: true
      });
    }
  }, [
    searchDuplicate,
    searchDuplicatePatient,
    setDuplicatedPatient,
    setValue,
    birthdate,
    firstName,
    lastName
  ]);
  if (!duplicatedPatient) {
    return null;
  }
  return (
    <Box sx={{ color: misc.warningText }}>
      <Stack direction="row" spacing={0.25}>
        <WarningAmberIcon sx={{ height: 24, pr: 1.5, pt: 1 }} />

        <Stack width="100%">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography fontSize={16} sx={{ mb: 1 }}>
              Patient {firstName} {lastName} with DOB{" "}
              {birthdate
                ? format(new Date(birthdate!), "MM/dd/yyyy")
                : "Not on File"}
              {duplicatedPatient.first_name.toLowerCase() ===
              firstName.toLowerCase()
                ? " already"
                : " might"}{" "}
              exist
              {duplicatedPatient.first_name.toLowerCase() ===
              firstName.toLowerCase()
                ? ""
                : ` (as ${duplicatedPatient.first_name?.trim()} ${duplicatedPatient.last_name?.trim()})`}
              .
            </Typography>
            <Tooltip
              title={
                <PatientSearchOption
                  option={duplicatedPatient}
                  sx={{
                    backgroundColor: misc.mutedPurple,
                    color: neutral[900]
                  }}
                />
              }
            >
              <Button
                size="small"
                variant="outlined"
                onClick={onClick}
                sx={{ minWidth: "180px" }}
              >
                Select {duplicatedPatient.first_name?.trim()}{" "}
                {duplicatedPatient.last_name?.trim()}
              </Button>
            </Tooltip>
          </Box>

          <Box display="flex" alignItems="center" gap={2}>
            <FormControlLabel
              sx={{ "& span": { fontSize: 16 } }}
              label="Are you sure you want to create a new patient?"
              control={
                <CheckboxFormField
                  testId="allow_create"
                  name="allow_create"
                  control={control}
                />
              }
            />
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
}
